import { Box, Center, Heading, HStack, Text, VStack } from "@chakra-ui/layout";
import { Button, Tooltip } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type { BudgetOrderByColumnEnum } from "@src/__generated__/graphql";
import type { WithIdProps } from "@src/components/HOC/with-id/withId";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  ButtonLink,
  type ButtonLinkProps,
  Card,
  DataTable,
  DatePicker,
  formatDate,
  LoadingOverlay,
} from "@src/components/ui-kit";
import { EditableText } from "@src/components/ui-kit/editable-text";
import { Icon } from "@src/components/ui-kit/Icon";
import { BACK_BUTTON_QUERY_PARAM_KEY } from "@src/components/ui-kit/NavBar/BackButton";
import { NavigationProject } from "@src/components/widgets/AppSubNav";
import { useStore } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { CreateBudgetMenu } from "../../budgets/export/CreateBudgetMenu";
import {
  AccountManagerRenderer,
  ActionsRenderer,
  ApprovalRenderer,
  AssignmentRenderer,
  DateRenderer,
  PriceRenderer,
  QuoteRenderer,
  StatusRenderer,
  VersionRenderer,
} from "../../budgets/export/components";
import type { BudgetModel } from "./models/budget";
import { BudgetStore } from "./store";

type BudgetTableRow = Record<string, unknown> & {
  id: string;
  budget: BudgetModel;
};

export const Budgets = observer(function Budgets({ id }: WithIdProps) {
  const appStore = useStore();
  const { store } = useLocalObservable(() => ({
    store: new BudgetStore(appStore),
  }));

  useEffect(() => {
    store.fetchProjectInfo(id);
    store.fetchBudgetGroups(id);
  }, [id]);

  const columns = useMemo<
    BodyColumnDef<BudgetTableRow, BudgetOrderByColumnEnum>[]
  >(
    () => [
      {
        id: "id",
        meta: { label: t`Number` },
        header: () => t`Number`,
        cell: ({ row }) => row.original.budget.id,
      },
      {
        id: "version",
        meta: { label: t`Version` },
        header: () => t`Version`,
        cell: ({ row }) => (
          <VersionRenderer version={row.original.budget.version} />
        ),
      },
      {
        id: "description",
        meta: { label: t`Description` },
        header: () => t`Description`,
        cell: ({ row }) => (
          <EditableText
            field={row.original.budget.description}
            onConfirm={row.original.budget.handleUpdateDescription}
            isEditable={!row.original.budget.isActionsLoading}
            isLoading={row.original.budget.isActionsLoading}
            size="sm"
          />
        ),
      },
      {
        id: "account-manager",
        meta: { label: t`Account manager` },
        header: () => t`Account manager`,
        cell: ({ row }) => (
          <AccountManagerRenderer
            accountManager={row.original.budget.account_manager}
          />
        ),
      },
      {
        id: "total",
        meta: { label: t`Total` },
        header: () => t`Total`,
        cell: ({ row }) => <PriceRenderer price={row.original.budget.price} />,
      },
      {
        id: "approval",
        meta: { label: t`Approval` },
        header: () => (
          <VStack spacing="0">
            <Trans>Approval</Trans>
            <Text>
              <Trans>(Internal | Client)</Trans>
            </Text>
          </VStack>
        ),
        cell: ({ row }) => <ApprovalRenderer budget={row.original.budget} />,
      },
      {
        id: "date",
        meta: { label: t`Date` },
        header: () => t`Date`,
        cell: ({ row }) => (
          <DateRenderer date={new Date(row.original.budget.created_at)} />
        ),
      },
      {
        id: "status",
        meta: { label: t`Status` },
        header: () => t`Status`,
        cell: ({ row }) => (
          <StatusRenderer status={row.original.budget.status} />
        ),
      },
      {
        id: "assignment",
        meta: { label: t`Assignment` },
        header: () => t`Assignment`,
        cell: ({ row }) => (
          <AssignmentRenderer
            budget={row.original.budget}
            onAssign={store.handleAssignInGroup}
          />
        ),
      },
      {
        id: "assignment_date",
        meta: { label: t`Assigned at` },
        header: () => t`Assigned at`,
        cell: ({ row }) =>
          row.original.budget.assignment_date ? (
            <Box pos="relative" cursor="pointer">
              <LoadingOverlay
                size="sm"
                isLoading={row.original.budget.isActionsLoading}
                position="absolute"
              >
                <DatePicker
                  usePortal
                  trigger={formatDate(
                    new Date(row.original.budget.assignment_date),
                  )}
                  selected={new Date(row.original.budget.assignment_date)}
                  disableDatesBefore={
                    store.projectInfo?.lowestPossibleDateForStartDateChange
                      ? new Date(
                          store.projectInfo
                            .lowestPossibleDateForStartDateChange,
                        )
                      : undefined
                  }
                  onChange={(val) => {
                    if (!val?.start) return;
                    row.original.budget.handleUpdateAssignmentDate(val.start);
                  }}
                />
              </LoadingOverlay>
            </Box>
          ) : null,
      },
      {
        id: "quotes",
        meta: { label: t`Quotes` },
        header: () => t`Quotes`,
        cell: ({ row }) => <QuoteRenderer budget={row.original.budget} />,
      },
      {
        id: ACTIONS_COLUMN_ID,
        meta: { label: t`Actions` },
        header: () => t`Actions`,
        size: 100,
        cell: ({ row }) => (
          <ActionsRenderer
            budget={row.original.budget}
            returnParams={`id=${id}`}
            onDelete={store.handleDeleteInGroup}
            projectId={id}
            isDeleteDisabled={row.original.budget.isActionsLoading}
          />
        ),
      },
    ],
    [id, store],
  );

  return (
    <Card>
      <HStack
        align="center"
        justify="space-between"
        px="4"
        borderBottom="1px"
        borderBottomColor="gray.200"
      >
        <NavigationProject id={id} />
        {store.budgetGroups.length > 0 && (
          <HStack spacing="2">
            <Tooltip
              isDisabled={!store.isDownloadReportButtonDisabled}
              label={t`Please, select a budget first`}
            >
              <Button
                colorScheme="grey"
                isDisabled={store.isDownloadReportButtonDisabled}
                isLoading={store.createQuoteMutator.isLoading.value}
                onClick={store.handleCreateQuote}
                variant="outline"
              >
                <Trans>Create Quote</Trans>
              </Button>
            </Tooltip>
            <CreateBudgetGroupButton
              projectId={id}
              brandId={store.projectInfo?.brand.id}
              backButtonParam={store.backButtonParam}
            />
          </HStack>
        )}
      </HStack>

      <LoadingOverlay
        isLoading={store.budgetGroupsFetcher.isLoading.value}
        position="absolute"
      >
        <Box minH="50dvh">
          {store.budgetGroups.length > 0
            ? store.budgetGroups.map((group, index) => (
                <Box
                  key={`${
                    // biome-ignore lint/suspicious/noArrayIndexKey: Budget groups don't have IDs
                    index
                  }-budget-group`}
                  _notLast={{
                    borderBottom: "1px",
                    borderBottomColor: "gray.200",
                  }}
                >
                  <DataTable.NavBar>
                    <HStack
                      px="1"
                      py="4"
                      align="center"
                      justify="space-between"
                    >
                      <EditableText
                        field={group.name}
                        isLoading={group.isNameEditLoading.value}
                        textStyles={{
                          fontWeight: "bold",
                          fontSize: "xl",
                        }}
                        onConfirm={async (name) => {
                          await group.handleUpdateGroupName(
                            name,
                            Number(id),
                            Number(group.number),
                          );
                        }}
                      />
                      <ButtonLink
                        minW="fit-content"
                        leftIcon={<Icon name="plus" />}
                        variant="outline"
                        colorScheme="violet"
                        href={{
                          pathname: "/budgets/proposals",
                          query: {
                            id: id,
                            project_id: id,
                            client_id: store.projectInfo?.brand.id,
                            returnParams: `id=${id}`,
                            budgetNumber: group.number ?? undefined,
                            [BACK_BUTTON_QUERY_PARAM_KEY]:
                              store.backButtonParam,
                            budget_id:
                              group.budgets.length > 0
                                ? group.budgets[0].id
                                : undefined,
                          },
                        }}
                      >
                        <Trans>New version</Trans>
                      </ButtonLink>
                    </HStack>
                  </DataTable.NavBar>
                  <DataTable.Body<BudgetTableRow, BudgetOrderByColumnEnum>
                    enableRowSelection
                    columns={columns}
                    data={group.budgets.map((budget) => ({
                      id: budget.id,
                      budget,
                    }))}
                    tableKey={store.tableKey}
                    onSelect={group.handleSelectIds}
                    selected={group.selectedIds}
                  />
                </Box>
              ))
            : !store.budgetGroupsFetcher.isLoading.value && (
                <Center w="full" minH="50dvh">
                  <VStack spacing="4">
                    <Heading size="lg">
                      <Trans>
                        No budget has been created for the project yet.
                      </Trans>
                    </Heading>
                    <CreateBudgetGroupButton
                      projectId={id}
                      brandId={store.projectInfo?.brand.id}
                      backButtonParam={store.backButtonParam}
                    />
                  </VStack>
                </Center>
              )}
        </Box>
      </LoadingOverlay>
    </Card>
  );
});

type CreateBudgetGroupButtonProps = {
  projectId: string;
  brandId?: string;
  size?: ButtonLinkProps["size"];
  backButtonParam: string;
};
const CreateBudgetGroupButton = observer(function CreateBudgetGroupButton({
  projectId,
  brandId,
  size,
  backButtonParam,
}: CreateBudgetGroupButtonProps) {
  return (
    <CreateBudgetMenu
      menuPlacement="left-start"
      listPlacement="left-start"
      createPathname="/projects/budgets/create"
      queryParams={{
        id: projectId,
        project_id: projectId,
        client_id: brandId,
        returnParams: `id=${projectId}`,
        [BACK_BUTTON_QUERY_PARAM_KEY]: backButtonParam,
      }}
    >
      <Button minW="fit-content" leftIcon={<Icon name="plus" />} size={size}>
        <Trans>New budget</Trans>
      </Button>
    </CreateBudgetMenu>
  );
});
